import { Controller } from "@hotwired/stimulus";
import Tagify from "@yaireo/tagify";

export default class extends Controller {
  static values = {
    url: String,
    initialData: Array,
    excludeSelectedIn: String,
  };

  static targets = [
    "input",
  ];

  connect() {
    this.initializeTagify();
    this.watchPartnerInput();
  }

  disconnect() {
    this.partnerInput?.removeEventListener("change", this.applyWhitelist);
    this.highlightObserver?.disconnect();
  }

  // Tagify replaces the input with its own markup. Turbo caches that markup and
  // restores it, then connect() runs again and wraps the already-wrapped input,
  // so every back-navigation adds another copy of the field. Hand the original
  // input back before the snapshot is taken.
  teardown() {
    this.tagify?.destroy();
    this.tagify = null;
  }

  initializeTagify() {
    if (!this.urlValue) {
      return; // Don't try to load tagify if no remote URL is specified
    }

    try {
      this.tagify = new Tagify(this.inputTarget, {
        whitelist: this.initialDataValue,
        enforceWhitelist: true,
        createInvalidTags: false,
        skipInvalid: true, // do not add invalid entries
        dropdown: {
          searchKeys: ["name"],
          maxItems: 20,
          classname: "tags-look",
          enabled: 0,
          closeOnSelect: false,
        },
        templates: {
          tag: (tagData) => tagData.tag_html,
          dropdownItem: (tagData) => tagData.dropdown_html,
          dropdownHeader: (_tagData) => ``,
        },
      });
    } catch (error) {
      console.error(`Unable to load groups`);
    }

    if (this.tagify) {
      this.exposeCombobox();
    }

    this.loadTagifyWhitelist();
  }

  // Tagify renders a plain textbox, so give it the combobox pattern Knox::ComboboxComponent uses
  exposeCombobox() {
    const combobox = this.tagify.DOM.input;
    const listbox = this.tagify.DOM.dropdown;
    const labelId = this.labelId();

    listbox.id = `${this.inputTarget.id}-listbox`;
    combobox.setAttribute("role", "combobox");
    combobox.setAttribute("aria-autocomplete", "list");
    combobox.setAttribute("aria-expanded", "false");
    combobox.removeAttribute("aria-multiline");
    combobox.removeAttribute("aria-placeholder");
    listbox.removeAttribute("aria-labelledby");
    if (labelId) {
      this.tagify.DOM.scope.setAttribute("role", "group");
      this.tagify.DOM.scope.setAttribute("aria-labelledby", labelId);
      combobox.setAttribute("aria-labelledby", labelId);
      listbox.setAttribute("aria-labelledby", labelId);
    }

    this.tagify.on("dropdown:show", this.markExpanded);
    this.tagify.on("dropdown:hide", this.markCollapsed);
    this.highlightObserver = new MutationObserver(this.followHighlightedOption);
    this.highlightObserver.observe(listbox, { subtree: true, attributeFilter: ["aria-selected"] });
  }

  labelId() {
    const label = this.inputTarget.labels?.[0];
    if (!label || !(label.id || this.inputTarget.id)) {
      return null;
    }

    label.id ||= `${this.inputTarget.id}_label`;
    return label.id;
  }

  markExpanded = () => {
    this.tagify.DOM.scope.removeAttribute("aria-expanded");
    this.tagify.DOM.input.setAttribute("aria-expanded", "true");
    this.tagify.DOM.input.setAttribute("aria-controls", this.tagify.DOM.dropdown.id);
  };

  markCollapsed = () => {
    this.tagify.DOM.scope.removeAttribute("aria-expanded");
    this.tagify.DOM.input.setAttribute("aria-expanded", "false");
    this.tagify.DOM.input.removeAttribute("aria-controls");
    this.tagify.DOM.input.removeAttribute("aria-activedescendant");
  };

  followHighlightedOption = () => {
    const combobox = this.tagify.DOM.input;
    const listbox = this.tagify.DOM.dropdown;
    const options = [...listbox.querySelectorAll("[role='option']")];
    const highlighted = options.findIndex((option) => option.getAttribute("aria-selected") === "true");
    if (highlighted < 0) {
      combobox.removeAttribute("aria-activedescendant");
      return;
    }

    const option = options[highlighted];
    option.id ||= `${listbox.id}-option-${highlighted}`;
    combobox.setAttribute("aria-activedescendant", option.id);
  };

  loadTagifyWhitelist() {
    fetch(this.urlValue, {
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        this.groups = data;
        this.applyWhitelist();
      });
  }

  watchPartnerInput() {
    if (!this.excludeSelectedInValue) {
      return;
    }

    this.partnerInput = document.getElementById(this.excludeSelectedInValue);
    this.partnerInput?.addEventListener("change", this.applyWhitelist);
  }

  applyWhitelist = () => {
    if (!this.tagify || !this.groups) {
      return;
    }

    const taken = this.partnerSelectedValues();
    this.tagify.whitelist = this.groups.filter((group) => !taken.has(String(group.value)));
  };

  partnerSelectedValues() {
    if (!this.partnerInput?.value) {
      return new Set();
    }

    try {
      return new Set(JSON.parse(this.partnerInput.value).map((tag) => String(tag.value)));
    } catch {
      return new Set();
    }
  }
}
